import { z } from 'zod';
import { phoneSchema, faxOptionalSchema } from '../shared/phone-fax';
// Shared frequency entry schema for all disciplines
export const frequencyEntrySchema = z
    .object({
    visits: z.string().min(1, 'Complete visit details or remove this entry'),
    per: z.preprocess((val) => (val == null || val === '' ? undefined : val), z.enum(['week', 'bi-weekly', 'month']).optional()),
    duration: z.string().optional(),
    prn: z.boolean().nullish(),
})
    .superRefine((data, ctx) => {
    if (!data.prn) {
        if (!data.per) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Select a frequency period', path: ['per'] });
        }
        if (!data.duration) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter the duration for this frequency', path: ['duration'] });
        }
    }
});
export const patientConsentValidationSchema = z.object({
    id: z.string(),
    signedUserId: z.string().min(1, 'required'),
    signedUserDate: z.string().min(1, 'required'),
    data: z
        .object({
        agencyName: z.string(),
        agencyShortName: z.string(),
        agencyAddress: z.string(),
        agencyAdministrator: z.string(),
        agencyAdministratorPhone: phoneSchema,
        agencyAdministratorFax: faxOptionalSchema,
        agencyHoursMonday: z.string().nullish(),
        agencyHoursTuesday: z.string().nullish(),
        agencyHoursWednesday: z.string().nullish(),
        agencyHoursThursday: z.string().nullish(),
        agencyHoursFriday: z.string().nullish(),
        agencyHoursSaturday: z.string().nullish(),
        agencyHoursSunday: z.string().nullish(),
        agencyChargeSN: z.string(),
        agencyChargePT: z.string(),
        agencyChargeOT: z.string(),
        agencyChargeST: z.string(),
        agencyChargeHHA: z.string(),
        agencyChargeMSW: z.string(),
        hotlineName: z.string(),
        hotlinePhone: phoneSchema,
        hotlineAddress: z.string(),
        language: z.string({ message: 'Language is required' }),
        informationNotRelease: z.string().optional(),
        informationNotReleaseTo: z.string().optional(),
        advanceDirectiveCompleted: z.string({ message: 'Please select an option' }).optional(),
        powerOfAttorney: z.string({ message: 'Please select an option' }).optional(),
        livingWill: z.string({ message: 'Please select an option' }).optional(),
        dnr: z.string({ message: 'Please select an option' }).optional(),
        otherAdvanceDirective: z.string().optional(),
        copyObtainedForChart: z.string().optional(),
        polst: z.string().optional(),
        molst: z.string().optional(),
        otherLegalDocument: z.string().optional(),
        surrogateDecisionMaker: z.string().optional(),
        medicalTreatmentPreferences: z.string().optional(),
        mentalHealthPreferences: z.string().optional(),
        culturalSocialPreferences: z.string().optional(),
        spiritualReligiousPreferences: z.string().optional(),
        advanceDirectiveChecklist: z.array(z.string()).min(1, 'requires at least one'),
        photographConsent: z.string({ message: 'Please select an option' }),
        patientSigned: z.literal(true, {
            errorMap: () => ({ message: 'Patient signature is required' }),
        }),
        snService: z.string().optional(),
        snFrequency: z.array(frequencyEntrySchema),
        ptService: z.string().optional(),
        ptFrequency: z.array(frequencyEntrySchema),
        otService: z.string().optional(),
        otFrequency: z.array(frequencyEntrySchema),
        stService: z.string().optional(),
        stFrequency: z.array(frequencyEntrySchema),
        hhaService: z.string().optional(),
        hhaFrequency: z.array(frequencyEntrySchema),
        mswService: z.string().optional(),
        mswFrequency: z.array(frequencyEntrySchema),
        dieticianService: z.string().optional(),
        dieticianFrequency: z.array(frequencyEntrySchema),
    })
        .superRefine((data, ctx) => {
        if (!data.advanceDirectiveChecklist?.includes('ad-check-1')) {
            if (!data.advanceDirectiveCompleted) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select an option', path: ['advanceDirectiveCompleted'] });
            }
            if (!data.powerOfAttorney) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select an option', path: ['powerOfAttorney'] });
            }
            if (!data.livingWill) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select an option', path: ['livingWill'] });
            }
            if (!data.dnr) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Please select an option', path: ['dnr'] });
            }
        }
        // Service and Frequency validation: at least one must be provided for each discipline
        const disciplines = [
            { service: 'snService', frequency: 'snFrequency', label: 'Skilled Nursing' },
            { service: 'hhaService', frequency: 'hhaFrequency', label: 'Home Health Aide' },
            { service: 'ptService', frequency: 'ptFrequency', label: 'Physical Therapy' },
            { service: 'otService', frequency: 'otFrequency', label: 'Occupational Therapy' },
            { service: 'stService', frequency: 'stFrequency', label: 'Speech Therapy' },
            { service: 'dieticianService', frequency: 'dieticianFrequency', label: 'Dietician' },
            { service: 'mswService', frequency: 'mswFrequency', label: 'Medical Social Worker' },
        ];
        for (const { service, frequency } of disciplines) {
            const hasService = Boolean(data[service]);
            const hasFrequency = data[frequency] && data[frequency].length > 0;
            if (!hasService && !hasFrequency) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Either service type or frequency is required',
                    path: [service],
                });
            }
        }
    }),
});
